/**
 * 调色板量化（PNG-8 导出补充，参照 Photoshop「存储为 Web 所用格式」）。
 *
 * Median cut：把所有出现过的颜色（RGBA 四维，alpha 也参与分裂——半透明
 * 像素跟不透明像素不该被硬凑进同一个调色板格）看成一堆点，每次挑"最该
 * 拆"的那个盒子，沿它跨度最大的通道、按像素数中位数切成两半，直到凑够
 * 目标色数或再拆不动为止；每个盒子的加权平均色就是一个调色板项。
 *
 * 纯 TS 实现——多颜色量化没有浏览器原生 API，也不引入新依赖（CLAUDE.md
 * 规则 7/9）。
 */

export interface Rgba {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}

export interface IndexedImage {
  /** 每像素一个字节，索引进 `palette`。 */
  readonly indices: Uint8Array;
  /** 长度 ≤ maxColors，按调色板序号排列。 */
  readonly palette: readonly Rgba[];
}

interface ColorPoint {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
  readonly count: number;
}

interface Box {
  points: ColorPoint[];
  totalCount: number;
}

function boxRange(box: Box): { channel: 0 | 1 | 2 | 3; range: number } {
  let minR = 255;
  let maxR = 0;
  let minG = 255;
  let maxG = 0;
  let minB = 255;
  let maxB = 0;
  let minA = 255;
  let maxA = 0;
  for (const p of box.points) {
    if (p.r < minR) minR = p.r;
    if (p.r > maxR) maxR = p.r;
    if (p.g < minG) minG = p.g;
    if (p.g > maxG) maxG = p.g;
    if (p.b < minB) minB = p.b;
    if (p.b > maxB) maxB = p.b;
    if (p.a < minA) minA = p.a;
    if (p.a > maxA) maxA = p.a;
  }
  const ranges = [maxR - minR, maxG - minG, maxB - minB, maxA - minA] as const;
  let channel: 0 | 1 | 2 | 3 = 0;
  let range = ranges[0];
  for (let c = 1; c < 4; c++) {
    if (ranges[c]! > range) {
      range = ranges[c]!;
      channel = c as 0 | 1 | 2 | 3;
    }
  }
  return { channel, range };
}

function channelValue(p: ColorPoint, channel: 0 | 1 | 2 | 3): number {
  return channel === 0 ? p.r : channel === 1 ? p.g : channel === 2 ? p.b : p.a;
}

/** 按像素数量占用最大的盒子优先拆——跟经典 median-cut 一致，视觉误差集中在色彩最丰富的区域最先被照顾到。 */
function splitBox(box: Box): [Box, Box] {
  const { channel } = boxRange(box);
  const sorted = [...box.points].sort((a, b) => channelValue(a, channel) - channelValue(b, channel));
  const half = box.totalCount / 2;
  let acc = 0;
  let cut = sorted.length - 1;
  for (let i = 0; i < sorted.length; i++) {
    acc += sorted[i]!.count;
    if (acc >= half) {
      cut = i;
      break;
    }
  }
  // 至少各留一个点，否则空盒子会在后续步骤里产生 NaN 平均色
  const at = Math.min(Math.max(cut + 1, 1), sorted.length - 1);
  const left = sorted.slice(0, at);
  const right = sorted.slice(at);
  return [
    { points: left, totalCount: left.reduce((s, p) => s + p.count, 0) },
    { points: right, totalCount: right.reduce((s, p) => s + p.count, 0) },
  ];
}

function averageColor(box: Box): Rgba {
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  for (const p of box.points) {
    r += p.r * p.count;
    g += p.g * p.count;
    b += p.b * p.count;
    a += p.a * p.count;
  }
  const n = box.totalCount;
  return {
    r: Math.round(r / n),
    g: Math.round(g / n),
    b: Math.round(b / n),
    a: Math.round(a / n),
  };
}

/** 找出图像里的全部去重颜色及各自出现次数——median-cut 拆的是这些点，不是逐像素。 */
function buildHistogram(pixels: Uint8Array): Map<number, ColorPoint> {
  const histogram = new Map<number, ColorPoint>();
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i]!;
    const g = pixels[i + 1]!;
    const b = pixels[i + 2]!;
    const a = pixels[i + 3]!;
    const key = (r << 24) | (g << 16) | (b << 8) | a;
    const existing = histogram.get(key);
    if (existing === undefined) {
      histogram.set(key, { r, g, b, a, count: 1 });
    } else {
      histogram.set(key, { ...existing, count: existing.count + 1 });
    }
  }
  return histogram;
}

function colorDistance(a: Rgba, b: Rgba): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  const da = a.a - b.a;
  return dr * dr + dg * dg + db * db + da * da;
}

/**
 * 生成调色板：median-cut 到 `maxColors`（2–256）个颜色。
 * 颜色种类本来就 ≤ maxColors 时直接原样返回，不做无意义的拆分。
 */
export function buildPalette(pixels: Uint8Array, maxColors: number): readonly Rgba[] {
  const histogram = buildHistogram(pixels);
  const points = [...histogram.values()];
  if (points.length <= maxColors) {
    return points.map((p) => ({ r: p.r, g: p.g, b: p.b, a: p.a }));
  }

  const boxes: Box[] = [{ points, totalCount: points.reduce((s, p) => s + p.count, 0) }];
  while (boxes.length < maxColors) {
    // 每轮挑像素数最多、且真的还能拆（含 ≥2 种颜色）的盒子来拆
    let target = -1;
    let targetCount = -1;
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i]!;
      if (box.points.length < 2) continue;
      if (box.totalCount > targetCount) {
        targetCount = box.totalCount;
        target = i;
      }
    }
    if (target < 0) break; // 所有盒子都拆到只剩一种颜色，凑不出更多调色板项了
    const [left, right] = splitBox(boxes[target]!);
    boxes.splice(target, 1, left, right);
  }
  return boxes.map(averageColor);
}

/**
 * 把像素映射到调色板：`dither` 关时最近色直接量化；开时 Floyd–Steinberg
 * 误差扩散——把这一像素"没表达出来"的颜色误差按权重甩给右/左下/下/右下
 * 还没处理的邻居，视觉上用相邻像素的抖动图案换取更接近原图的平均色，
 * 是真实 Photoshop"扩散仿色"选项要还原的效果。
 */
export function quantizeToPalette(
  pixels: Uint8Array,
  width: number,
  height: number,
  palette: readonly Rgba[],
  dither: boolean,
): Uint8Array {
  const indices = new Uint8Array(width * height);
  // 精确色→调色板下标的缓存：quantizeToPalette 常常在同一批像素里反复见到
  // 同一个颜色（大片纯色区域），线性扫 256 项调色板的成本不该被重复付
  const exactCache = new Map<number, number>();
  const nearest = (r: number, g: number, b: number, a: number): number => {
    const key = (r << 24) | (g << 16) | (b << 8) | a;
    const cached = exactCache.get(key);
    if (cached !== undefined) return cached;
    let best = 0;
    let bestDist = Infinity;
    const target: Rgba = { r, g, b, a };
    for (let i = 0; i < palette.length; i++) {
      const d = colorDistance(target, palette[i]!);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    exactCache.set(key, best);
    return best;
  };

  if (!dither) {
    for (let i = 0, p = 0; p < pixels.length; i++, p += 4) {
      indices[i] = nearest(pixels[p]!, pixels[p + 1]!, pixels[p + 2]!, pixels[p + 3]!);
    }
    return indices;
  }

  // 误差扩散需要浮点误差缓冲（RGBA 四通道），逐行处理、就地累加进下一行
  const errR = new Float32Array(width * height);
  const errG = new Float32Array(width * height);
  const errB = new Float32Array(width * height);
  const errA = new Float32Array(width * height);
  const clamp = (v: number): number => Math.min(255, Math.max(0, v));

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const p = i * 4;
      const r = clamp(pixels[p]! + errR[i]!);
      const g = clamp(pixels[p + 1]! + errG[i]!);
      const b = clamp(pixels[p + 2]! + errB[i]!);
      const a = clamp(pixels[p + 3]! + errA[i]!);
      const idx = nearestFloat(palette, r, g, b, a);
      indices[i] = idx;
      const chosen = palette[idx]!;
      const dr = r - chosen.r;
      const dg = g - chosen.g;
      const db = b - chosen.b;
      const da = a - chosen.a;
      // 标准 Floyd–Steinberg 权重：右 7/16，左下 3/16，下 5/16，右下 1/16
      if (x + 1 < width) {
        errR[i + 1]! += (dr * 7) / 16;
        errG[i + 1]! += (dg * 7) / 16;
        errB[i + 1]! += (db * 7) / 16;
        errA[i + 1]! += (da * 7) / 16;
      }
      if (y + 1 < height) {
        if (x > 0) {
          errR[i + width - 1]! += (dr * 3) / 16;
          errG[i + width - 1]! += (dg * 3) / 16;
          errB[i + width - 1]! += (db * 3) / 16;
          errA[i + width - 1]! += (da * 3) / 16;
        }
        errR[i + width]! += (dr * 5) / 16;
        errG[i + width]! += (dg * 5) / 16;
        errB[i + width]! += (db * 5) / 16;
        errA[i + width]! += (da * 5) / 16;
        if (x + 1 < width) {
          errR[i + width + 1]! += (dr * 1) / 16;
          errG[i + width + 1]! += (dg * 1) / 16;
          errB[i + width + 1]! += (db * 1) / 16;
          errA[i + width + 1]! += (da * 1) / 16;
        }
      }
    }
  }
  return indices;
}

/** 误差扩散路径专用：颜色是浮点误差累加后的结果，不走整数 exactCache。 */
function nearestFloat(palette: readonly Rgba[], r: number, g: number, b: number, a: number): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const c = palette[i]!;
    const dr = r - c.r;
    const dg = g - c.g;
    const db = b - c.b;
    const da = a - c.a;
    const d = dr * dr + dg * dg + db * db + da * da;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/** 一步到位：量化 + 映射。 */
export function buildIndexedImage(
  pixels: Uint8Array,
  width: number,
  height: number,
  maxColors: number,
  dither: boolean,
): IndexedImage {
  const palette = buildPalette(pixels, Math.min(256, Math.max(2, maxColors)));
  const indices = quantizeToPalette(pixels, width, height, palette, dither);
  return { indices, palette };
}
